import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableLoading
} from "@/components/ui/table";
import {
  CaretSortIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { Route as OutOfStockRoute } from "@/routes/(inventory)/branch/_layout/reports/out-of-stock";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { useState } from "react";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { useOutOfStockData } from "../-hooks/useOutOfStockData";

const parseDate = (
  date:
    | string
    | number
    | Date
    | dayjs.Dayjs
    | { Time: string; Valid: boolean }
    | null
    | undefined
) => {
  if (typeof date === "string") {
    return dayjs(date, "YYYY-MM-DD"); // Adjust format as necessary
  }
  return dayjs(); // Default to current date if invalid
};

const Component = () => {
  const searchParams = OutOfStockRoute.useSearch();
  const { data, isLoading, error, isFetching } = useOutOfStockData();
  const navigate = useNavigate();

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("expiry_date");

  const totalPages = data?.totalCount && data.totalCount > 0 ? Math.ceil(data.totalCount / searchParams.pageSize) : 1;

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = data?.items ? data.items.slice().sort((a, b) => {
    if (orderBy === "expiry_date") {
      const dateA = parseDate(a.expiryDate?.toDate()).valueOf();
      const dateB = parseDate(b.expiryDate?.toDate()).valueOf();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  }) : [];

  // TableCell and TableHead wrappers for consistent styling
  const StyledTableCell = ({ className = "", ...props }) => (
    <TableCell className={cn(tableCellClass, "py-2 font-medium", className)} {...props} />
  );
  const StyledTableHead = ({ className = "", ...props }) => (
    <TableHead className={cn(tableHeadClass, className)} {...props} />
  );

  const columns = [
    { key: "trade_name", label: "Trade Name" },
    { key: "batch_number", label: "Batch Number" },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "unit_type", label: "Unit Type" },
    { key: "barcode", label: "Barcode" },
    { key: "price", label: "Price" },
    { key: "cost", label: "Cost" },
    { key: "actual_quantity", label: "Actual Quantity" },
    { key: "sold_quantity", label: "Sold Quantity" },
    { key: "available_quantity", label: "Available Quantity" },
    { key: "last_sold_on", label: "Last Sold On" },
  ];
  const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

  return (
    <div className="overflow-y-scroll h-screen pb-48">
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => {
                  navigate({
                    to: "/branch/reports",
                  });
                }}
                style={{
                  cursor: "pointer",
                }}
              >
                Reports Overview
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Out Of Stock</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <hr className="my-4" />
      <div className="w-full grid mb-4">
        <Tabs defaultValue={searchParams.type}>
          <TabsList>
            <TabsTrigger
              value={"MEDICAL"}
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    type: "MEDICAL",
                  },
                });
              }}
            >
              Medical
            </TabsTrigger>
            <TabsTrigger
              value={"NON_MEDICAL"}
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    type: "NON_MEDICAL",
                  },
                });
              }}
            >
              Non Medical
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="mt-4 border rounded-lg bg-white overflow-hidden">
        {isLoading ? (
          <TableLoading columns={columns} visibleColumns={visibleColumns} />
        ) : !data?.items || data.items.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-gray-500">No entries found</div>
          </div>
        ) : (
          <>
            <Table className="text-sm">
          <TableHeader>
            <TableRow>
              <StyledTableHead>Trade Name</StyledTableHead>
              <StyledTableHead>Batch Number</StyledTableHead>
              <StyledTableHead
                onClick={() => handleRequestSort("expiry_date")}
                style={{ cursor: "pointer" }}
              >
                <div className="flex items-center">
                  <span>Expiry Date</span>
                  {orderBy === "expiry_date" && (
                    <CaretSortIcon
                      className={`ml-2 h-4 w-4 ${order === "asc" ? "rotate-180" : ""}`}
                    />
                  )}
                </div>
              </StyledTableHead>
              <StyledTableHead>Unit Type</StyledTableHead>
              <StyledTableHead>Barcode</StyledTableHead>
              <StyledTableHead>Price</StyledTableHead>
              <StyledTableHead>Cost</StyledTableHead>
              <StyledTableHead>Actual Quantity</StyledTableHead>
              <StyledTableHead>Sold Quantity</StyledTableHead>
              <StyledTableHead>Available Quantity</StyledTableHead>
              <StyledTableHead>Last Sold On</StyledTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((item, index) => {
              const isLastRow = index === sortedData.length - 1;
              return (
                <TableRow key={index}>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.tradeName}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.batchNumber}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                    {item.isValidExpiryDate && dayjs(item.expiryDate?.toDate()).isValid()
                      ? dayjs(item.expiryDate?.toDate()).format("DD/MM/YYYY")
                      : "—"}
                  </StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.unitType}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.barcode}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.sellingPrice)}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.costFromSupplier)}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                    {item.inputQuantity?.bonus! + item.inputQuantity?.original!}
                  </StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                    {item.inputQuantity?.bonus! + item.inputQuantity?.original! - item.availableQuantity}
                  </StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                    {item.availableQuantity}
                  </StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                    {"-"}
                  </StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </>
        )}
        <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
        <p>{data?.items && data.items.length > 0 ? data.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
          <div className="flex items-center gap-1">
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: 1
                  }
                });
              }}
              disabled={searchParams.page === 1}
            >
              <DoubleArrowLeftIcon className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: searchParams.page - 1
                  }
                });
              }}
              disabled={searchParams.page === 1}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </PaginationButton>
            {(() => {
              // TODO: Replace with backend-provided full_count for real pagination
              const getPageNumbers = () => {
                const pages = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                  }
                } else {
                  pages.push(1);
                  if (searchParams.page > 3) pages.push('...');
                  for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                    pages.push(i);
                  }
                  if (searchParams.page < totalPages - 2) pages.push('...');
                  pages.push(totalPages);
                }
                return pages;
              };
              return getPageNumbers().map((p, idx) => (
                p === '...'
                  ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                  : <PaginationButton
                      key={p}
                      active={searchParams.page === p}
                      onClick={() => {
                        navigate({
                          search: {
                            ...searchParams,
                            page: typeof p === 'number' ? p : searchParams.page
                          }
                        });
                      }}
                    >
                      <span className="text-sm">{p}</span>
                    </PaginationButton>
              ));
            })()}
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: searchParams.page + 1
                  }
                });
              }}
              disabled={searchParams.page >= totalPages}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: 1
                  }
                });
              }}
              disabled={searchParams.page >= totalPages}
            >
              <DoubleArrowRightIcon className="h-4 w-4" />
            </PaginationButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Component;
